import { useState } from "react";
import { UserPlus, ShieldCheck, Upload, ArrowLeft, Search, RefreshCcw, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { http, notify, errorMessage } from "../api";

/**
 * Public Sales Associate self-registration + status/resubmit page.
 * Route: /register (see App.js).
 *
 * Three modes:
 *   - "new"       → collect profile + KYC (POST /auth/register)
 *   - "check"     → identifier + password (POST /auth/registration/status)
 *   - "resubmit"  → shown after check when status=correction_required
 */
export default function Register() {
  const initialMode = (typeof window !== "undefined"
    && sessionStorage.getItem("reg_mode") === "check") ? "check" : "new";
  if (typeof window !== "undefined") sessionStorage.removeItem("reg_mode");
  const [mode, setMode] = useState(initialMode); // new | check | status | resubmit
  const [status, setStatus] = useState(null);

  if (mode === "check")     return <CheckStatusForm setMode={setMode} setStatus={setStatus} />;
  if (mode === "status")    return <StatusView status={status} setMode={setMode} />;
  if (mode === "resubmit")  return <ResubmitForm status={status} setMode={setMode} />;
  return <NewRegisterForm setMode={setMode} setStatus={setStatus} />;
}

/* -------------------------------------------------------------------- */

function NewRegisterForm({ setMode, setStatus }) {
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", pan: "",
  });
  const [panDoc, setPanDoc] = useState(null);
  const [aadhaarDoc, setAadhaarDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSelfie = (e) => {
    const f = e.target.files?.[0] || null;
    setSelfie(f);
    setSelfiePreview(f ? URL.createObjectURL(f) : "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!panDoc || !aadhaarDoc) return notify.error("Both PAN and Aadhaar documents are required");
    if (!selfie) return notify.error("A selfie photo is required");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("pan_doc", panDoc);
    fd.append("aadhaar_doc", aadhaarDoc);
    fd.append("selfie", selfie);
    setBusy(true);
    try {
      const r = await http.post("/auth/register", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({
        approval_status: r.data.approval_status,
        email: form.email, name: form.name,
      });
      setMode("status");
    } catch (err) {
      notify.error(err, errorMessage(err) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="login-page" data-testid="register-page">
      <div className="login-aside">
        <div className="brand-mark">RX</div>
        <p className="eyebrow">SALES ASSOCIATE</p>
        <h1>Join the<br /><em>DSA Connect</em> team.</h1>
        <p className="login-copy">
          Fill in your details and upload your KYC (PAN + Aadhaar). Your
          workspace unlocks once an administrator approves your account.
        </p>
      </div>
      <form className="login-form" onSubmit={submit} data-testid="register-form">
        <div>
          <p className="eyebrow">CREATE ACCOUNT</p>
          <h2><UserPlus size={18} /> Register</h2>
          <p className="muted" style={{ fontSize: 12 }}>
            Bold labels are required. Both KYC uploads are mandatory.
          </p>
        </div>
        <div className="register-grid">
          <label><strong>Full name</strong>
            <input data-testid="reg-name" required value={form.name} onChange={set("name")} /></label>
          <label><strong>Mobile</strong>
            <input data-testid="reg-mobile" required value={form.mobile} onChange={set("mobile")}
                   inputMode="tel" placeholder="9876543210" /></label>
          <label><strong>Email</strong>
            <input data-testid="reg-email" required type="email"
                   value={form.email} onChange={set("email")} /></label>
          <label>PAN number
            <input data-testid="reg-pan" value={form.pan} onChange={set("pan")}
                   placeholder="ABCDE1234F" style={{ textTransform: "uppercase" }} /></label>
        </div>
        <p className="muted" style={{ fontSize: 11, margin: "6px 0 0" }}>
          <strong>Note:</strong> Your login Employee ID will be generated automatically
          and shown on the next screen. Default password is <code>123456</code> —
          change it after your first sign-in.
        </p>
        <div style={{ marginTop: 4 }}>
          <p className="eyebrow"><ShieldCheck size={12} /> KYC DOCUMENTS</p>
          <p className="muted" style={{ fontSize: 11, margin: "0 0 8px" }}>
            JPG / PNG / WEBP / PDF · max 5 MB each · both are required.
          </p>
          <div className="register-grid">
            <label><strong>PAN document</strong>
              <input data-testid="reg-pan-doc" required type="file" accept="image/*,application/pdf"
                     onChange={(e) => setPanDoc(e.target.files?.[0] || null)} />
              {panDoc && <span className="muted" style={{ fontSize: 11 }}>{panDoc.name}</span>}
            </label>
            <label><strong>Aadhaar document</strong>
              <input data-testid="reg-aadhaar-doc" required type="file" accept="image/*,application/pdf"
                     onChange={(e) => setAadhaarDoc(e.target.files?.[0] || null)} />
              {aadhaarDoc && <span className="muted" style={{ fontSize: 11 }}>{aadhaarDoc.name}</span>}
            </label>
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <p className="eyebrow"><ShieldCheck size={12} /> PROFILE SELFIE</p>
          <p className="muted" style={{ fontSize: 11, margin: "0 0 8px" }}>
            JPG / PNG / WEBP · max 5 MB · this becomes your profile photo across the CRM.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {selfiePreview && (
              <img src={selfiePreview} alt="Selfie preview"
                   data-testid="reg-selfie-preview"
                   style={{ width: 72, height: 72, borderRadius: "50%",
                            objectFit: "cover", border: "2px solid var(--red)" }} />
            )}
            <label style={{ flex: 1 }}><strong>Selfie photo</strong>
              <input data-testid="reg-selfie" required type="file"
                     accept="image/jpeg,image/png,image/webp"
                     capture="user"
                     onChange={onSelfie} />
              {selfie && <span className="muted" style={{ fontSize: 11 }}>{selfie.name}</span>}
            </label>
          </div>
        </div>
        <button data-testid="register-submit" className="primary-button"
                disabled={busy || !panDoc || !aadhaarDoc || !selfie}>
          {busy ? "Uploading…" : <><Upload size={14} /> Submit for approval</>}
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <a href="/" data-testid="register-signin-link" className="muted">← Back to sign-in</a>
          <button type="button" className="link-button"
            onClick={() => setMode("check")} data-testid="register-check-status-link">
            <Search size={12} /> Already applied? Check status
          </button>
        </div>
      </form>
    </main>
  );
}

/* -------------------------------------------------------------------- */

function CheckStatusForm({ setMode, setStatus }) {
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post("/auth/registration/status",
        { identifier: ident, password });
      setStatus(r.data);
      setMode("status");
    } catch (err) {
      notify.error(err, errorMessage(err) || "Could not fetch status");
    } finally { setBusy(false); }
  };

  return (
    <main className="login-page" data-testid="register-check-page">
      <div className="login-aside">
        <div className="brand-mark">RX</div>
        <p className="eyebrow">CHECK REGISTRATION STATUS</p>
        <h1>Where's my<br /><em>account?</em></h1>
        <p className="login-copy">
          Enter the same email or mobile and password you used to register
          and we'll show you the latest decision.
        </p>
      </div>
      <form className="login-form" onSubmit={submit} data-testid="check-form">
        <div>
          <p className="eyebrow">CHECK STATUS</p>
          <h2><Search size={18} /> Registration status</h2>
        </div>
        <label>Email or mobile
          <input data-testid="check-identifier" required value={ident}
                 onChange={(e) => setIdent(e.target.value)} /></label>
        <label>Password
          <input data-testid="check-password" required type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)} /></label>
        <button data-testid="check-submit" className="primary-button" disabled={busy}>
          {busy ? "Checking…" : <><Search size={14} /> Check status</>}
        </button>
        <button type="button" className="link-button" onClick={() => setMode("new")}
                data-testid="check-back-to-register">← Back to registration</button>
      </form>
    </main>
  );
}

/* -------------------------------------------------------------------- */

function StatusView({ status, setMode }) {
  const s = (status?.approval_status || "").toLowerCase();
  const pill =
    s === "approved"            ? { icon: CheckCircle2,   text: "APPROVED",           tone: "green" } :
    s === "correction_required" ? { icon: AlertTriangle,  text: "CORRECTION NEEDED",  tone: "amber" } :
    s === "rejected"            ? { icon: AlertTriangle,  text: "REJECTED",           tone: "red" } :
                                  { icon: Clock,          text: "PENDING REVIEW",     tone: "blue" };
  const Icon = pill.icon;
  return (
    <main className="login-page" data-testid="register-status-page">
      <div className="login-aside">
        <div className="brand-mark">RX</div>
        <p className="eyebrow">REGISTRATION STATUS</p>
        <h1>{status?.name || "You"}</h1>
        <p className="login-copy">
          {s === "approved" && "Your account is live — sign in to start working."}
          {s === "pending" && "Our team is reviewing your KYC documents. You'll be notified as soon as we're done."}
          {s === "correction_required" && "An administrator needs updated details. Read the note below and resubmit."}
          {s === "rejected" && "Your registration was rejected. Reach out to your business head for next steps."}
        </p>
      </div>
      <div className="login-form">
        <div>
          <p className="eyebrow">STATUS</p>
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}
              data-testid="status-heading">
            <Icon size={20} /> {pill.text}
          </h2>
          {status?.employee_id && (
            <p className="muted" style={{ fontSize: 12 }}>Employee ID · {status.employee_id}</p>
          )}
        </div>
        {(status?.approval_status || "").toLowerCase() === "pending" && !status?.employee_code && (
          <div
            data-testid="reg-await-admin-card"
            style={{
              padding: 14, border: "1px solid #cbd5e1", background: "#f1f5f9",
              borderRadius: 8, fontSize: 13, color: "#334155",
            }}
          >
            <p className="eyebrow" style={{ marginBottom: 8, color: "#0f172a" }}>WHAT HAPPENS NEXT</p>
            <ol style={{ marginLeft: 18, lineHeight: 1.6 }}>
              <li>Our admin will verify your KYC documents.</li>
              <li>Once approved, the admin will share your login <strong>Employee ID</strong> and <strong>password</strong> with you directly.</li>
              <li>Use those to sign in — you'll be asked to change the password on first login.</li>
            </ol>
          </div>
        )}
        {status?.rejection_reason && (
          <div
            data-testid="admin-note"
            style={{
              padding: 12, border: "1px solid #e4b4af", background: "#fff0ee",
              color: "var(--red)", fontSize: 13, lineHeight: 1.4,
            }}
          >
            <strong style={{ display: "block", marginBottom: 4 }}>Administrator note</strong>
            {status.rejection_reason}
          </div>
        )}
        {status?.can_resubmit && (
          <button
            data-testid="status-resubmit-button"
            className="primary-button"
            onClick={() => setMode("resubmit")}
          >
            <RefreshCcw size={14} /> Resubmit registration
          </button>
        )}
        {s === "approved" && (
          <a href="/" data-testid="status-signin-link" className="primary-button"
             style={{ textDecoration: "none", textAlign: "center" }}>
            <ArrowLeft size={14} /> Sign in
          </a>
        )}
        <button type="button" className="link-button" onClick={() => setMode("new")}
                data-testid="status-new-registration">← Back to registration</button>
      </div>
    </main>
  );
}

/* -------------------------------------------------------------------- */

function ResubmitForm({ status, setMode }) {
  const [password, setPassword] = useState("");
  const [form, setForm] = useState({
    name: status?.name || "", email: status?.email || "",
    mobile: status?.mobile || "", pan: "",
  });
  const [panDoc, setPanDoc] = useState(null);
  const [aadhaarDoc, setAadhaarDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return notify.error("Enter your password to confirm");
    const fd = new FormData();
    fd.append("identifier", form.email || form.mobile);
    fd.append("password", password);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (panDoc) fd.append("pan_doc", panDoc);
    if (aadhaarDoc) fd.append("aadhaar_doc", aadhaarDoc);
    if (selfie) fd.append("selfie", selfie);
    setBusy(true);
    try {
      await http.post("/auth/registration/resubmit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notify.success("Resubmitted — awaiting admin review");
      setMode("status");
    } catch (err) {
      notify.error(err, errorMessage(err) || "Resubmit failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="login-page" data-testid="register-resubmit-page">
      <div className="login-aside">
        <div className="brand-mark">RX</div>
        <p className="eyebrow">RESUBMIT REGISTRATION</p>
        <h1>Update &amp; try<br /><em>again.</em></h1>
        <p className="login-copy">
          Fix the details the administrator asked about, re-upload any
          replacement KYC documents, then confirm with your password.
        </p>
        {status?.rejection_reason && (
          <div
            style={{ marginTop: 16, padding: 12, background: "#fff0ee",
                     color: "var(--red)", border: "1px solid #e4b4af", fontSize: 13 }}
          >
            <strong>Administrator note</strong>
            <div>{status.rejection_reason}</div>
          </div>
        )}
      </div>
      <form className="login-form" onSubmit={submit} data-testid="resubmit-form">
        <div>
          <p className="eyebrow">UPDATE DETAILS</p>
          <h2><RefreshCcw size={18} /> Resubmit for approval</h2>
          <p className="muted" style={{ fontSize: 12 }}>
            Leave fields blank if they haven't changed.
          </p>
        </div>
        <div className="register-grid">
          <label>Full name<input data-testid="rs-name" value={form.name} onChange={set("name")} /></label>
          <label>Mobile<input data-testid="rs-mobile" value={form.mobile} onChange={set("mobile")} /></label>
          <label>Email<input data-testid="rs-email" type="email" value={form.email} onChange={set("email")} /></label>
          <label>PAN<input data-testid="rs-pan" value={form.pan} onChange={set("pan")}
                          placeholder="ABCDE1234F" style={{ textTransform: "uppercase" }} /></label>
          <label>Replace PAN document
            <input data-testid="rs-pan-doc" type="file" accept="image/*,application/pdf"
                   onChange={(e) => setPanDoc(e.target.files?.[0] || null)} />
            {panDoc && <span className="muted" style={{ fontSize: 11 }}>{panDoc.name}</span>}
          </label>
          <label>Replace Aadhaar document
            <input data-testid="rs-aadhaar-doc" type="file" accept="image/*,application/pdf"
                   onChange={(e) => setAadhaarDoc(e.target.files?.[0] || null)} />
            {aadhaarDoc && <span className="muted" style={{ fontSize: 11 }}>{aadhaarDoc.name}</span>}
          </label>
          <label>Replace selfie
            <input data-testid="rs-selfie" type="file" accept="image/jpeg,image/png,image/webp"
                   capture="user"
                   onChange={(e) => setSelfie(e.target.files?.[0] || null)} />
            {selfie && <span className="muted" style={{ fontSize: 11 }}>{selfie.name}</span>}
          </label>
        </div>
        <label><strong>Confirm with your password</strong>
          <input data-testid="rs-password" required type="password" value={password}
                 onChange={(e) => setPassword(e.target.value)} /></label>
        <button data-testid="rs-submit" className="primary-button" disabled={busy}>
          {busy ? "Resubmitting…" : <><Upload size={14} /> Resubmit</>}
        </button>
        <button type="button" className="link-button" onClick={() => setMode("status")}
                data-testid="rs-back-to-status">← Back to status</button>
      </form>
    </main>
  );
}
